// src/pages/EmailVerification.tsx
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/useToast";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface EmailVerificationProps {
  email: string;
}

const EmailVerification = ({ email }: EmailVerificationProps) => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleResendVerification = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });
      if (error) throw error;
      toast({
        title: "Verification email sent!",
        description: "Please check your inbox.",
      });
    } catch (err) {
      toast({
        title: "Error sending verification email",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    setLoading(true);
    try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
            throw new Error("Failed to retrieve user session. Please sign in again.");
        }

        if (!session) {
            navigate('/auth');
            return;
        }
        
        const user = session.user;

      if (user) {
        // Manually refresh user data from the server
        await supabase.auth.refreshSession();
        const { data: { user: refreshedUser } } = await supabase.auth.getUser();

        if (refreshedUser?.email_confirmed_at) {
          toast({
            title: "Email verified!",
            description: "You can now complete your profile.",
          });
          // navigate('/welcome');
          navigate('/');
        } else {
          toast({
            title: "Email not verified",
            description: "Please check your inbox for the verification link.",
            variant: "destructive",
          });
        }
      }
    } catch (err) {
      toast({
        title: "Error checking verification status",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-primary">Verify Your Email</CardTitle>
          <CardDescription>
            We've sent a verification link to <strong>{email}</strong>. Please check your inbox and follow the instructions to activate your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={handleCheckVerification} className="w-full" disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            I've Verified My Email
          </Button>
          <Button variant="outline" onClick={handleResendVerification} className="w-full" disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Resend Verification Email
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailVerification;
